/**
 * POST FACTURA DIALOG
 *
 * Aparece justo después de crear una factura.
 * Paso 1: ¿Desea registrar el pago ahora?
 * Paso 2: ¿Desea imprimir el recibo? (solo si la factura está pagada)
 */

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle, CreditCard, Printer, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PagoFormDialog } from "./pago-form-dialog";
import type { Factura, FacturaListItem } from "@/hooks/use-facturacion";

// =============================================================================
// Helpers
// =============================================================================

/** Convierte Factura (detail) a FacturaListItem (shape que espera PagoFormDialog) */
function toListItem(f: Factura): FacturaListItem {
  return {
    id: f.id,
    numero: f.numero,
    ordenes_count: f.ordenes_count,
    cliente: f.cliente?.id ?? null,
    cliente_nombre: f.cliente?.nombre ?? null,
    paciente: f.paciente,
    paciente_nombre: f.paciente_nombre,
    nombre_cliente: f.nombre_cliente,
    rtn_cliente: f.rtn_cliente,
    sucursal: f.sucursal,
    sucursal_nombre: f.sucursal_nombre,
    subtotal: f.subtotal,
    descuento: f.descuento,
    descuento_pagos_directos: f.descuento_pagos_directos,
    total: f.total,
    estado: f.estado,
    tipo_documento: f.tipo_documento,
    fecha: f.fecha,
    esta_pagada: f.esta_pagada,
    total_pagado: f.total_pagado,
    saldo_pendiente: f.saldo_pendiente,
    is_active: f.is_active,
    created_at: f.created_at,
  };
}

// =============================================================================
// Props
// =============================================================================

interface PostFacturaDialogProps {
  factura: Factura | null;
  onClose: () => void;
  onPrint: (facturaId: number) => void;
}

type Step = "pagar" | "imprimir" | "done";

// =============================================================================
// Component
// =============================================================================

export function PostFacturaDialog({ factura, onClose, onPrint }: PostFacturaDialogProps) {
  const { t } = useTranslation();
  // Si la factura ya está pagada (ej: todas las órdenes eran pago directo), saltar directo a imprimir
  const initialStep: Step = factura?.esta_pagada ? "imprimir" : "pagar";
  const [step, setStep] = useState<Step>(initialStep);
  const [isPagoOpen, setIsPagoOpen] = useState(false);
  // Trackea si la factura quedó pagada tras registrar un pago exitoso
  const [estaPagada, setEstaPagada] = useState(factura?.esta_pagada ?? false);

  const open = factura !== null;
  const facturaId = factura?.id;
  const facturaEstaPagada = factura?.esta_pagada ?? false;

  // Sincronizar step y estaPagada cuando se abre una nueva factura
  useEffect(() => {
    if (facturaId !== undefined) {
      setStep(facturaEstaPagada ? "imprimir" : "pagar");
      setEstaPagada(facturaEstaPagada);
    }
  }, [facturaId, facturaEstaPagada]);

  const handlePagarSi = () => {
    setIsPagoOpen(true);
  };

  // Usuario no quiere pagar ahora — cerrar directamente sin ofrecer imprimir
  const handlePagarNo = () => {
    handleDone();
  };

  const handlePagoSuccess = (result?: import("@/hooks/use-facturacion").PagoResult) => {
    setIsPagoOpen(false);
    // Usar esta_pagada del resultado del backend
    if (result) {
      setEstaPagada(result.esta_pagada);
      // Solo pasar a imprimir si el pago quedó completo
      if (result.esta_pagada) {
        setStep("imprimir");
      } else {
        // Pago parcial - no ofrecer imprimir, cerrar el diálogo
        handleDone();
      }
    } else {
      // Sin resultado, cerrar sin imprimir
      handleDone();
    }
  };

  const handlePagoClosed = () => {
    setIsPagoOpen(false);
    // Solo ofrecer imprimir si la factura ya está pagada
    if (estaPagada) {
      setStep("imprimir");
    } else {
      handleDone();
    }
  };

  const handleImprimirSi = () => {
    if (factura) onPrint(factura.id);
    handleDone();
  };

  const handleImprimirNo = () => {
    handleDone();
  };

  const handleDone = () => {
    setStep(factura?.esta_pagada ? "imprimir" : "pagar");
    setEstaPagada(factura?.esta_pagada ?? false);
    onClose();
  };

  if (!factura) return null;

  return (
    <>
      <Dialog open={open && !isPagoOpen} onOpenChange={(v) => !v && handleDone()}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <DialogTitle>{t("facturacion.postFactura.title")}</DialogTitle>
            </div>
            <DialogDescription>
              {t("facturacion.postFactura.numero", { numero: factura.numero })}
            </DialogDescription>
          </DialogHeader>

          {step === "pagar" && (
            <>
              <div className="flex items-start gap-3 rounded-md border p-4">
                <CreditCard className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm font-medium">{t("facturacion.postFactura.pagarTitle")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("facturacion.postFactura.pagarDesc", {
                      total: parseFloat(factura.saldo_pendiente).toFixed(2),
                    })}
                  </p>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handlePagarNo}>
                  <X className="h-4 w-4 mr-1.5" />
                  {t("common.no")}
                </Button>
                <Button onClick={handlePagarSi}>
                  <CreditCard className="h-4 w-4 mr-1.5" />
                  {t("common.yes")}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "imprimir" && (
            <>
              {estaPagada ? (
                <>
                  <div className="flex items-start gap-3 rounded-md border p-4">
                    <Printer className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        {t("facturacion.postFactura.imprimirTitle")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("facturacion.postFactura.imprimirDesc")}
                      </p>
                    </div>
                  </div>
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={handleImprimirNo}>
                      <X className="h-4 w-4 mr-1.5" />
                      {t("common.no")}
                    </Button>
                    <Button onClick={handleImprimirSi}>
                      <Printer className="h-4 w-4 mr-1.5" />
                      {t("common.yes")}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 rounded-md border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30 p-4">
                    <AlertCircle className="h-5 w-5 mt-0.5 text-orange-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        {t("facturacion.postFactura.sinPagoTitle")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t("facturacion.postFactura.sinPagoDesc")}
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleDone}>{t("common.close")}</Button>
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Pago dialog — se monta sobre el post-factura dialog */}
      <PagoFormDialog
        open={isPagoOpen}
        onOpenChange={handlePagoClosed}
        factura={toListItem(factura)}
        onSuccess={handlePagoSuccess}
      />
    </>
  );
}
